import React, { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character, 
  // CharacterFeatureSource, 
  Modifier, 
  UpgradableNumber,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import CheckBox from "@components/input/CheckBox";

import ViewItem from "@dndComponents/display/ViewItem";

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterFeatureFromSource from "@dndComponents/character/features/CharacterFeatureFromSource";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';

interface Props {
  character: Character;
  disabled: boolean;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterAttunement({ 
  character,
  disabled,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [open, setOpen] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [view, setView] = useState("Features");
  const [ping, setPing] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      character.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    }
  });

  const recalculated = () => {
    // console.log("Attunement");
    setReloading(true);
  }
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  // const onReload = (change: boolean, do_all: boolean = false) => {
  //   if (change) {
  //     onChange(do_all);
  //   }
  //   setReloading(true);
  // }

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderAttunementFeatures = () => {
    const attunement_features = character.modifier_features.filter(cf => 
      cf.floating_feature instanceof Modifier && 
        ["Attunement Slots"].includes(cf.floating_feature.modifies));
    return (
      <>
        <Grid
          className="header"
          size={{
            xs: 12
          }}>
          Attunement Features
        </Grid>
        { attunement_features.sort((a,b) => { return a.name.localeCompare(b.name) }).map((char_feature, key) => {
          const source = character.all_char_feature_sources.find(o => 
            o.true_id === char_feature.source_true_id);
          if (source) {
            return (
              <Grid
                key={key}
                size={{
                  xs: 12
                }}>
                <CharacterFeatureFromSource
                  character={character}
                  obj={char_feature}
                  source={source}
                />
              </Grid>
            );
          }
          return null;
        })}
      </>
    );
  }

  const renderAttunementsInput = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="centered header"
          size={{
            xs: 12
          }}>
          Manage Attunement
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <CenteredMenu
            options={["Features","Override Attunement"]}
            selected={view}
            onSelect={setView}
          />
        </Grid>
        { renderView() }
      </Grid>
    );
  }

  const renderView = () => {
    switch(view) {
      case "Features": return renderAttunementFeatures();
      default: return renderCustomization();
    }
  }

  const enabled = !disabled && api.user !== null && (character.owner_id === api.user._id || (character.campaign !== null && character.campaign.owner_id === api.user._id));
   
  const renderItemsNeedingAttunement = () => {
    // TODO: Make the CheckBox disabled if they don't meet the Attunement Limitations,
    // and have a tooltip explaining that's why
    return character.items.filter(o => 
      (o.attunement) || 
      (o.char_magic_item_keywords.filter(o2 => o2.attunement).length > 0)).map((item, key) => {
      // debugger;
      const can_attune = char_util.canAttune(character, item);
      return (
        <Grid
          key={key}
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          <Grid
            size={{
              xs: 1
            }}>
            { !reloading &&
              <CheckBox 
                tooltip={ can_attune === "true" ? "" : can_attune }
                value={item.attuned} 
                disabled={ !enabled || (!item.attuned && (character.attuned_items.length >= character.max_attuned_items || can_attune !== "true")) }
                onChange={(value: boolean) => {
                  if (value) {
                    connect_util.attuneItem(character, item);
                  } else {
                    connect_util.unattuneItem(character, item);
                  }
                  character.changed();
                  console.log("Attunement Change");
                  console.log(character);
                  setPing(!ping);
                  char_util.repopulateCharFeatureBases(character);
                }} 
              />
            }
          </Grid>
          <Grid
            size={{
              xs: 11
            }}>
            { !reloading && <ViewItem item={item} character={character} /> }
          </Grid>
        </Grid>
      );
    });
  } 

  const renderCustomization = () => {
    return (
      <>
        <Grid
          size={{
            xs: 6
          }}>
          <CheckBox
            name="Override?"
            value={ character.attunement_customization !== null }
            onChange={(changed: boolean) => {
              if (changed) {
                character.attunement_customization = new UpgradableNumber();
              } else {
                character.attunement_customization = null;
                character.changed();
                char_util.repopulateCharFeatureBases(character);
              }
              // console.log("Override Change");
              setPing(!ping);
            }}
          />
        </Grid>
        { character.attunement_customization &&
          <Grid size={6}>
            <UpgradableNumberBox
              name="Number"
              value={character.attunement_customization}
              onChange={(changed: UpgradableNumber) => {
                character.attunement_customization = changed.clone();
                character.changed();
                char_util.repopulateCharFeatureBases(character);
                setPing(!ping);
              }}
            />
          </Grid>
        }
      </>
    );
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        style={{
          fontSize: "14px",
        }}
        className={`header ${ disabled ? "" : "clickable"}`}
        onClick={() => {
          if (!disabled) {
            setOpen(true);
          }
        }}
        size={{
          xs: 12
        }}>
        &nbsp;Attunement
      </Grid>
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        &nbsp;Attuned Items ({ character.attuned_items.length === character.max_attuned_items ? `${character.max_attuned_items}` : `${character.attuned_items.length}/${character.max_attuned_items}` })
      </Grid>
      { renderItemsNeedingAttunement() }
      <ModalOrDrawer
        open={ open } 
        // drawer_only
        onClose={() => {
          setOpen(false);
          character.subscription_holder.modalClosed();
        }}>
        { open && renderAttunementsInput() }
      </ModalOrDrawer>
    </Grid>
  );
}
